// EnneagramChart — premium 9-petal organic flower chart (Apple Health / Nothing OS /
// Linear feel), built from independent cubic-Bézier petal shapes with visible gaps —
// NOT a pie/donut. See geometry.ts for all path math and animations.ts for the
// dependency-free (no Framer Motion) tween/easing used for value + mount animation.

import React, { useId, useMemo, useState } from "react";
import { Petal } from "./Petal";
import { ENN_EASE_CSS, useTweenedValues } from "./animations";
import {
  CENTER,
  INNER_RADIUS,
  OUTER_RADIUS_MAX,
  VIEWBOX,
  argmax,
  buildPetalPath,
  getPetalAxisPoints,
  getPetalLabelPoint,
  getPetalSlots,
  valueToOuterRadius,
} from "./geometry";
import type { EnneagramChartProps, PetalGeometry } from "./types";
import "./enneagram.css";

export const ENNEAGRAM_LABELS_SK = [
  "Reformátor",
  "Pomocník",
  "Úspešný",
  "Individualista",
  "Bádateľ",
  "Lojálny",
  "Nadšenec",
  "Vyzývateľ",
  "Mierotvorca",
];

// Reduced ~40% from the original 8° default (see EnneagramTile.jsx for the
// matching per-count formula) — petals sit close enough to almost touch, so
// the flower reads as one sculpted whole instead of separated blobs, while
// still leaving a clean gap for the center "rounded star" negative space.
const DEFAULT_GAP = 5;
const DEFAULT_COLOR_VARS = [
  "var(--enn-1)", "var(--enn-2)", "var(--enn-3)", "var(--enn-4)", "var(--enn-5)",
  "var(--enn-6)", "var(--enn-7)", "var(--enn-8)", "var(--enn-9)",
];

export function EnneagramChart({
  values,
  labels,
  colors,
  size = 280,
  gap = DEFAULT_GAP,
  animated = true,
  showValues = false,
  showCenter = true,
  selected: selectedProp,
  onSelect,
  className,
  theme,
}: EnneagramChartProps) {
  const n = values.length;
  const resolvedLabels = labels ?? (n === 9 ? ENNEAGRAM_LABELS_SK : undefined);
  const resolvedColors = colors ?? DEFAULT_COLOR_VARS;

  const [internalSelected, setInternalSelected] = useState<number | null>(null);
  const selected = selectedProp !== undefined ? selectedProp : internalSelected;

  const tweened = useTweenedValues(values, 650, animated);

  const slots = useMemo(() => getPetalSlots(n, gap), [n, gap]);

  // Stable per-mounted-chart id prefix so gradient <defs> ids never collide
  // when two EnneagramChart instances are on screen at once (e.g. the tile
  // preview + its detail Sheet both rendering simultaneously).
  const uid = useId();

  const petals: PetalGeometry[] = useMemo(
    () =>
      slots.map((slot) => {
        const outerRadius = valueToOuterRadius(tweened[slot.index] ?? 0, INNER_RADIUS, OUTER_RADIUS_MAX);
        const { hub, tip } = getPetalAxisPoints(slot.centerAngle, outerRadius, INNER_RADIUS);
        return {
          index: slot.index,
          path: buildPetalPath({
            centerAngle: slot.centerAngle,
            halfAngle: slot.halfAngle,
            outerRadius,
          }),
          centerAngle: slot.centerAngle,
          outerRadius,
          labelPoint: getPetalLabelPoint(slot.centerAngle, outerRadius),
          hub,
          tip,
        };
      }),
    [slots, tweened],
  );

  const dominant = useMemo(() => argmax(values), [values]);
  const dominantLabel = resolvedLabels?.[dominant];
  const dominantValue = Math.round(values[dominant] ?? 0);

  const handleSelect = (index: number) => {
    if (selectedProp === undefined) {
      setInternalSelected((prev) => (prev === index ? null : index));
    }
    onSelect?.(index);
  };

  return (
    <div
      className={["enn-chart", className].filter(Boolean).join(" ")}
      style={{ width: "100%", maxWidth: size }}
      data-enn-theme={theme}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        width="100%"
        role="img"
        aria-label="Enneagram — 9 typov osobnosti"
        style={{ display: "block", "--enn-ease": ENN_EASE_CSS } as React.CSSProperties}
      >
        {/* One depth gradient per petal, aimed exactly along that petal's own
            hub→tip axis (userSpaceOnUse — NOT objectBoundingBox, since petals
            point in different directions and a bbox-relative gradient would
            skew for anything not pointing straight up). Pure SVG paint —
            no filter/blur anywhere, so this is Mali-safe by construction. */}
        <defs>
          {petals.map((p) => (
            <linearGradient
              key={p.index}
              id={`enn-grad-${uid}-${p.index}`}
              gradientUnits="userSpaceOnUse"
              x1={p.hub.x}
              y1={p.hub.y}
              x2={p.tip.x}
              y2={p.tip.y}
              style={{ "--enn-petal-color": resolvedColors[p.index % resolvedColors.length] } as React.CSSProperties}
            >
              <stop offset="0%" className="enn-petal__stop-base" />
              <stop offset="55%" className="enn-petal__stop-mid" />
              <stop offset="100%" className="enn-petal__stop-tip" />
            </linearGradient>
          ))}
        </defs>
        {petals.map((p, i) => (
          <Petal
            key={i}
            index={p.index}
            path={p.path}
            color={resolvedColors[p.index % resolvedColors.length]}
            gradientId={`enn-grad-${uid}-${p.index}`}
            label={resolvedLabels?.[p.index]}
            value={values[p.index] ?? 0}
            showValue={showValues}
            selected={selected === p.index}
            dimmed={selected !== null && selected !== undefined && selected !== p.index}
            onSelect={handleSelect}
            labelPoint={p.labelPoint}
          />
        ))}
        {showCenter && (
          <g className="enn-center" style={{ pointerEvents: "none" }}>
            <circle cx={CENTER.x} cy={CENTER.y} r={INNER_RADIUS - 6} className="enn-center__hub" />
            <text x={CENTER.x} y={CENTER.y - 10} textAnchor="middle" dominantBaseline="middle" className="enn-center__num">
              {dominant + 1}
            </text>
            {dominantLabel && (
              <text x={CENTER.x} y={CENTER.y + 4} textAnchor="middle" dominantBaseline="middle" className="enn-center__label">
                {dominantLabel.toUpperCase()}
              </text>
            )}
            <text x={CENTER.x} y={CENTER.y + 16} textAnchor="middle" dominantBaseline="middle" className="enn-center__value">
              {dominantValue}%
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
